/**
 * =============================================================================
 * MLYN GLIGHTBOX INTEGRATION - Lightbox Utilities
 * =============================================================================
 * Version: GLightbox 3.3.1 (Latest)
 * Documentation: https://biati-digital.github.io/glightbox/
 * 
 * Optimized configuration untuk Mlyn sections dengan best practices
 * =============================================================================
 */

/**
 * Default GLightbox configuration untuk Mlyn sections
 * Dioptimasi untuk performance dan UX terbaik
 */
const mlynGlightboxDefaults = {
  // Touch & interaction
  touchNavigation: true,
  touchFollowAxis: true,
  
  // Keyboard
  keyboardNavigation: true,
  closeOnOutsideClick: true,
  
  // Effects
  openEffect: 'zoom',
  closeEffect: 'zoom',
  slideEffect: 'slide',
  
  // UI
  moreText: 'See more',
  moreLength: 60,
  
  // Images
  zoomable: true,
  draggable: true,
  dragAutoSnap: false,
  dragToleranceX: 40,
  dragToleranceY: 65,
  
  // Video
  autoplayVideos: true,
  autofocusVideos: false,
  
  // Performance
  preload: true,
  loop: false,
  
  // Accessibility
  closeButton: true,
  
  // Video player (Plyr)
  plyr: {
    css: 'https://cdn.plyr.io/3.7.8/plyr.css',
    js: 'https://cdn.plyr.io/3.7.8/plyr.polyfilled.js',
    config: {
      ratio: '16:9',
      hideControls: true,
      youtube: {
        noCookie: true,
        rel: 0,
        showinfo: 0,
        iv_load_policy: 3
      },
      vimeo: {
        byline: false,
        portrait: false,
        title: false,
        speed: true,
        transparent: false
      }
    }
  }
};

/**
 * Product Images Gallery
 * Untuk product image gallery dengan zoom
 */
function mlynGlightboxProductGallery(selector = '.glightbox-product', options = {}) {
  const config = {
    ...mlynGlightboxDefaults,
    selector: selector,
    zoomable: true,
    draggable: true,
    loop: true,
    ...options
  };
  
  return GLightbox(config);
}

/**
 * Simple Image Gallery
 * Basic image lightbox tanpa extra features
 */
function mlynGlightboxImageGallery(selector = '.glightbox-image', options = {}) {
  const config = {
    ...mlynGlightboxDefaults,
    selector: selector,
    ...options
  };
  
  return GLightbox(config);
}

/**
 * Video Lightbox
 * Untuk YouTube, Vimeo, atau self-hosted videos
 */
function mlynGlightboxVideo(selector = '.glightbox-video', options = {}) {
  const config = {
    ...mlynGlightboxDefaults,
    selector: selector,
    autoplayVideos: true,
    ...options
  };
  
  return GLightbox(config);
}

/**
 * Inline Content Lightbox
 * Untuk menampilkan inline HTML content
 */
function mlynGlightboxInline(selector = '.glightbox-inline', options = {}) {
  const config = {
    ...mlynGlightboxDefaults,
    selector: selector,
    zoomable: false,
    draggable: false,
    ...options
  };
  
  return GLightbox(config);
}

/**
 * Mixed Gallery (Images + Videos)
 * Gallery yang bisa handle images dan videos sekaligus
 */
function mlynGlightboxMixed(selector = '.glightbox-mixed', options = {}) {
  const config = {
    ...mlynGlightboxDefaults,
    selector: selector,
    loop: true,
    ...options
  };
  
  return GLightbox(config);
}

/**
 * Auto-initialize GLightbox dengan data attributes
 * Usage: <a href="image.jpg" data-mlyn-lightbox="product-gallery" data-gallery="gallery1">
 */
function mlynInitGlightbox() {
  // Product gallery
  if (document.querySelector('[data-mlyn-lightbox="product-gallery"]')) {
    mlynGlightboxProductGallery('[data-mlyn-lightbox="product-gallery"]');
  }
  
  // Image gallery
  if (document.querySelector('[data-mlyn-lightbox="image-gallery"]')) {
    mlynGlightboxImageGallery('[data-mlyn-lightbox="image-gallery"]');
  }
  
  // Video
  if (document.querySelector('[data-mlyn-lightbox="video"]')) {
    mlynGlightboxVideo('[data-mlyn-lightbox="video"]');
  }
  
  // Inline content
  if (document.querySelector('[data-mlyn-lightbox="inline"]')) {
    mlynGlightboxInline('[data-mlyn-lightbox="inline"]');
  }
  
  // Mixed
  if (document.querySelector('[data-mlyn-lightbox="mixed"]')) {
    mlynGlightboxMixed('[data-mlyn-lightbox="mixed"]');
  }
  
  // Default glightbox class
  if (document.querySelector('.glightbox:not([data-mlyn-lightbox])')) {
    GLightbox({
      ...mlynGlightboxDefaults,
      selector: '.glightbox:not([data-mlyn-lightbox])'
    });
  }
}

/**
 * Load GLightbox library dynamically
 * Hanya load ketika dibutuhkan
 */
function mlynLoadGlightbox(callback) {
  // Check if already loaded
  if (window.GLightbox) {
    if (callback) callback();
    return;
  }
  
  // Load CSS
  const css = document.createElement('link');
  css.rel = 'stylesheet';
  css.href = 'https://cdn.jsdelivr.net/npm/glightbox@3.3.1/dist/css/glightbox.min.css';
  document.head.appendChild(css);
  
  // Load JS
  const script = document.createElement('script');
  script.src = 'https://cdn.jsdelivr.net/npm/glightbox@3.3.1/dist/js/glightbox.min.js';
  script.onload = () => {
    mlynInitGlightbox();
    if (callback) callback();
  };
  document.head.appendChild(script);
}

// Auto-load if lightbox elements exist
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (document.querySelector('[data-mlyn-lightbox], .glightbox')) {
      mlynLoadGlightbox();
    }
  });
} else {
  if (document.querySelector('[data-mlyn-lightbox], .glightbox')) {
    mlynLoadGlightbox();
  }
}

// Theme editor support
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    if (document.querySelector('[data-mlyn-lightbox], .glightbox')) {
      mlynLoadGlightbox();
    }
  });
}

// Export untuk manual use
window.MlynGlightbox = {
  load: mlynLoadGlightbox,
  init: mlynInitGlightbox,
  productGallery: mlynGlightboxProductGallery,
  imageGallery: mlynGlightboxImageGallery,
  video: mlynGlightboxVideo,
  inline: mlynGlightboxInline,
  mixed: mlynGlightboxMixed,
  defaults: mlynGlightboxDefaults,
};
